// frontend/src/components/GlobalSearch.js

import React, { useEffect, useMemo, useRef, useState } from "react";
import { MagnifyingGlass, X, Hash } from "phosphor-react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "api";
import { DEFAULT_CIRCLE_IMAGE, DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";

const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tags, setTags] = useState([]); // tag suggestions
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const nav = useNavigate();

  const closeSearch = () => {
    setOpen(false);
    setQ("");
    setUsers([]);
    setGroups([]);
    setTags([]);
    setError("");
    setLoading(false);
  };

  const debouncedQ = useMemo(() => q.trim(), [q]);
  const isTagMode = debouncedQ.startsWith("#");
  const tagPrefix = useMemo(
    () => (isTagMode ? debouncedQ.replace(/^#/, "").trim().toLowerCase() : ""),
    [debouncedQ, isTagMode]
  );

  useEffect(() => {
    if (!open) return;

    if (!debouncedQ) {
      setUsers([]); setGroups([]); setTags([]); setLoading(false); setError("");
      return;
    }

    // ---------- TAG MODE ----------
    if (isTagMode) {
      // Clear user/circle columns in tag mode
      setUsers([]); setGroups([]);
      setError("");

      // No prefix -> no suggestions
      if (!tagPrefix) {
        setTags([]);
        setLoading(false);
        return;
      }

      let cancelled = false;
      (async () => {
        setLoading(true);
        try {
          const token = localStorage.getItem("token");
          const headers = token ? { Authorization: `Bearer ${token}` } : {};

          // Try several endpoints for tag autocomplete; normalize to [{name: 'solidcore'}, ...]
          const tryEndpoints = [
            { url: `/api/tags/search`, params: { prefix: tagPrefix } },

            // fallbacks
            { url: `/activities/tags/search`, params: { prefix: tagPrefix } },
            { url: `/tags/search`, params: { prefix: tagPrefix } },
            { url: `/tags`, params: { prefix: tagPrefix } },
            { url: `/api/tags`, params: { prefix: tagPrefix } },
            { url: `/tags/popular`, params: {} },
          ];

          let found = null;
          for (const ep of tryEndpoints) {
            try {
              const res = await api.get(ep.url, { params: ep.params, headers });
              const raw = res?.data;
              if (!raw) continue;

              // Accept arrays of strings or arrays of objects with .name
              let list = Array.isArray(raw) ? raw : raw?.items;
              if (!Array.isArray(list)) continue;

              // If we hit /tags/popular, filter by prefix
              if (ep.url.includes("/popular")) {
                list = list.filter((t) => {
                  const n = (t?.name ?? t)?.toString().toLowerCase();
                  return n.startsWith(tagPrefix);
                });
              }

              const normalized = list
                .map((t) => {
                  const name = (t?.name ?? t)?.toString().toLowerCase().trim();
                  return name ? { name } : null;
                })
                .filter(Boolean);

              // Only keep tags that START with the prefix (no showing "#so" itself unless it exists)
              const prefixOnly = normalized.filter((t) => t.name.startsWith(tagPrefix));

              // Dedupe
              const uniq = Array.from(new Set(prefixOnly.map((t) => t.name))).map((n) => ({ name: n }));

              found = uniq;
              break;
            } catch {
              // try next
            }
          }

          if (!cancelled) {
            // If we couldn't discover a tag endpoint, show nothing (no fake "#so")
            setTags(Array.isArray(found) ? found.slice(0, 10) : []);
          }
        } catch (e) {
          if (!cancelled) {
            console.error("tag search failed", e?.response?.data || e);
            setTags([]);
            setError("Search failed");
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();

      return () => { cancelled = true; };
    }

    // ---------- NORMAL MODE (users + circles) ----------
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        const hdrs = token ? { Authorization: `Bearer ${token}` } : {};
        const [uRes, gRes] = await Promise.all([
          api.get(`/users/search`, { params: { q: debouncedQ }, headers: hdrs }),
          api.get(`/groups/search`, { params: { q: debouncedQ }, headers: hdrs }),
        ]);
        if (cancelled) return;
        setUsers(Array.isArray(uRes.data) ? uRes.data : []);
        setGroups(Array.isArray(gRes.data) ? gRes.data : []);
        setTags([]);
      } catch (err) {
        if (cancelled) return;
        console.error("search failed", err?.response?.data || err);
        setError("Search failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, debouncedQ, isTagMode, tagPrefix]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 0); }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeSearch(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!debouncedQ) return;
    if (isTagMode && tagPrefix) {
      closeSearch();
      nav(`/tags/${encodeURIComponent(tagPrefix)}`);
      return;
    }
  };

  const ResultRow = ({ to, avatar, title, subtitle, fallback }) => (
    <Link
      to={to}
      onClick={closeSearch}
      className="flex items-center gap-3 -ml-1.5 pl-1.5 pr-0.5 !min-h-0 !min-w-0 !py-1.5 hover:bg-gray-50 rounded-3xl"
    >
      <img src={avatar || fallback} alt="" className="w-8 h-8 rounded-full object-cover" />
      <div className="min-w-0">
        <div className="font-medium truncate">{title}</div>
        {subtitle && <div className="text-xs text-gray-500 truncate">{subtitle}</div>}
      </div>
    </Link>
  );

  const TagRow = ({ name }) => (
    <button
      type="button"
      onClick={() => { closeSearch(); nav(`/tags/${encodeURIComponent(name)}`); }}
      className="inline-flex items-center gap-3 !min-w-0 !min-h-0 !px-3 !py-1.5 h-8 hover:bg-gray-50 rounded-3xl text-left"
    >
      <div className="min-w-0">
        <div className="font-medium truncate">#{name}</div>
      </div>
    </button>
  );

  return (
    <div className="relative">
      <button
        onClick={() => (open ? closeSearch() : setOpen(true))}
        className="p-2 rounded-lg"
        aria-label="Search"
      >
        <MagnifyingGlass size={22} />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/30" onClick={closeSearch}>
          <div className="max-w-2xl mx-auto mt-10" onClick={(e) => e.stopPropagation()}>
            {/* Search bar */}
            <div className="mx-4 bg-white rounded-3xl shadow-lg px-4 py-0 sm:py-3 flex items-center gap-3">
              <MagnifyingGlass size={20} className="text-gray-500" />
              <form onSubmit={handleSubmit} className="flex-1">
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search members, circles, or #tags"
                  className="w-full outline-none"
                />
              </form>
              <button className="p-1 rounded hover:bg-gray-100" onClick={closeSearch} aria-label="Close search">
                <X size={18} />
              </button>
            </div>

            {/* Results (only show after the user types) */}
            {debouncedQ !== "" && (
              <div className="mx-4 mt-2 bg-white rounded-3xl shadow-xl p-4">
                {loading && <div className="p-3 text-sm text-gray-500">Searching…</div>}
                {error && <div className="p-3 text-sm text-red-600">{error}</div>}

                {/* TAG MODE: show only real tags that start with the prefix */}
                {isTagMode && (
                  <div>
                    <div className="text-xs font-semibold text-blue-800 px-1 mb-1">Tags</div>
                    <div className="space-y-1">
                      {tags.length > 0 ? (
                        tags.slice(0, 10).map((t) => <TagRow key={t.name} name={t.name} />)
                      ) : (
                        <div className="px-1.5 py-1 text-xs text-gray-400">
                          {tagPrefix ? `No tags starting with “#${tagPrefix}”` : "Type a tag - e.g. #piano"}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* NORMAL MODE */}
                {!isTagMode && !loading && !error && (users.length > 0 || groups.length > 0) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Members */}
                    <div>
                      <div className="text-xs font-semibold text-blue-800 px-1 mb-1">Members</div>
                      <div className="space-y-0">
                        {users.slice(0, 6).map((u) => (
                          <ResultRow
                            key={`u-${u.id}`}
                            to={`/profile/${u.id}`}
                            avatar={u.profile_pic_url}
                            title={u.name || u.username || `User #${u.id}`}
                            subtitle={u.email}
                            fallback={DEFAULT_PROFILE_IMAGE}
                          />
                        ))}
                        {users.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-400">No members found</div>
                        )}
                      </div>
                    </div>

                    {/* Circles */}
                    <div>
                      <div className="text-xs font-semibold text-blue-800 px-1 mb-1">Circles</div>
                      <div className="space-y-0">
                        {groups.slice(0, 6).map((g) => (
                          <ResultRow
                            key={`g-${g.id}`}
                            to={`/circles/${g.id}`}
                            avatar={g.image_url || g.icon_url}
                            title={g.name || `Circle #${g.id}`}
                            subtitle={`${g.member_count ?? ""} members`}
                            fallback={DEFAULT_CIRCLE_IMAGE}
                          />
                        ))}
                        {groups.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-400">No circles found</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {!isTagMode && !loading && !error && q && users.length === 0 && groups.length === 0 && (
                  <div className="p-3 text-sm text-gray-400">No results for “{q}”.</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
